<script lang="ts">
  import { computed, inject, provide, type Ref, type InjectionKey } from 'vue'
  import { globalUtils } from '@gat-ship/utils'
  import { useSessionStorage } from '@vueuse/core'
  import axios from 'axios'
  import { jwtDecode } from 'jwt-decode'
  import { getBaseApiUrl } from '../../../http-client'
  import { API_REQUEST_HEADERS } from '../../../http-client/api-request-headers'
  import { useRouteQuery } from '@gat-ship/lib/router'
  import { FEATURE_FLAGS } from '../../../feature-flag'

  // Constants for session storage keys
  export const SUPPORT_SESSION_STORAGE_KEYS = {
    SESSION_ID: 'Gatship-Support-Session-Id',
    SESSION_TOKEN: 'Gatship-Support-Session-Token'
  } as const

  /** The key for the support session ID in the route query. */
  export const SUPPORT_SESSION_ROUTE_QUERY_KEY = 'support-session-id'

  export function useSupportSessionFromRouteQuery(
    supportSessionId: Ref<string | null>,
    setSupportSessionId: (sessionId: string | null) => void
  ) {
    // Use centralized support session storage

    // Call useRouteQuery here after the router is ready
    const sessionIdFromRouteQuery = useRouteQuery<string | undefined>(
      SUPPORT_SESSION_ROUTE_QUERY_KEY
    )

    /** Will initialize the support session from the route query or the session storage.
     *
     * If there is no support session id nothing will happen.
     */
    async function getSupportSessionIdFromRouteQuery() {
      // Get the session id from the route query or the session storage.
      const sessionId = sessionIdFromRouteQuery.value ?? supportSessionId.value
      if (sessionId && typeof sessionId === 'string') {
        setSupportSessionId(sessionId)
        sessionIdFromRouteQuery.value = undefined
      }
    }

    return {
      getSupportSessionIdFromRouteQuery
    }
  }

  /**
   * Centralized composable for managing support session storage.
   *
   * This composable provides a consistent interface for accessing and managing
   * support session data across the application, ensuring all components use
   * the same storage keys and reactive references.
   *
   * @returns An object containing reactive support session storage references and utilities
   *
   * @example
   * ```typescript
   * const { supportSessionId, hasSupportSession, clearSupportSession } = useSupportSessionStorage();
   *
   * // Check if there's an active support session
   * if (hasSupportSession.value) {
   *   console.log('Active session:', supportSessionId.value);
   * }
   *
   * // Clear the session
   * clearSupportSession();
   * ```
   */
  export function useSupportSessionStorage() {
    // Reactive session storage for support session ID
    const supportSessionId = useSessionStorage<string | null>(
      SUPPORT_SESSION_STORAGE_KEYS.SESSION_ID,
      null
    )

    // Reactive session storage for support session token
    const supportSessionToken = useSessionStorage<string | null>(
      SUPPORT_SESSION_STORAGE_KEYS.SESSION_TOKEN,
      null
    )

    // Computed property to check if the current session is a support session
    const isSupportSession = computed(() => {
      return (
        supportSessionId.value !== null && supportSessionId.value !== undefined
      )
    })

    /**
     * Sets the support session ID in storage.
     *
     * @param sessionId - The support session ID to store
     */
    function setSupportSessionId(sessionId: string | null) {
      supportSessionId.value = sessionId
    }

    /**
     * Sets the support session token in storage.
     *
     * @param token - The support session token to store
     */
    function setSupportSessionToken(token: string | null) {
      supportSessionToken.value = token
    }

    /**
     * Clears both support session ID and token from storage.
     */
    function clearSupportSession() {
      supportSessionId.value = null
      supportSessionToken.value = null
    }

    /**
     * Gets the raw support session ID value (non-reactive).
     * Useful for scenarios where you need the value but don't want reactivity.
     *
     * @returns The current support session ID or null
     */
    function getSupportSessionId(): string | null {
      return supportSessionId.value
    }

    /**
     * Gets the raw support session token value (non-reactive).
     * Useful for scenarios where you need the value but don't want reactivity.
     *
     * @returns The current support session token
     */
    function getSupportSessionToken(): string | null {
      return supportSessionToken.value
    }

    function isSupportSessionTokenExpired(token: Readonly<string>) {
      const decodedToken = jwtDecode<{ iat: number; exp: number }>(token)
      if (!decodedToken) {
        throw new Error('Invalid support session token', {
          cause: { code: 'support_session_no_token' }
        })
      }
      const { exp } = decodedToken
      const expiresInSeconds = exp - Math.floor(Date.now() / 1000)
      if (FEATURE_FLAGS.IS_DEV) {
        console.log(
          `support session token expires in ${expiresInSeconds} seconds`
        )
      }
      return expiresInSeconds <= 30
    }

    async function fetchSupportSessionTokenIfExpired(
      authorizationToken: string
    ) {
      const supportSessionId = getSupportSessionId()
      if (!supportSessionId || typeof supportSessionId !== 'string') {
        throw new Error('No support session ID found in storage.')
      }
      const supportSessionToken = getSupportSessionToken()
      if (
        !supportSessionToken ||
        isSupportSessionTokenExpired(supportSessionToken)
      ) {
        // Refresh the support session token.
        try {
          const result = await axios.post<{ expiresIn: number; token: string }>(
            `${getBaseApiUrl()}/support/sessions/${supportSessionId}/token/`,
            undefined,
            {
              headers: {
                [API_REQUEST_HEADERS.GATSHIP_COMMAND_ID]:
                  globalUtils.functions.uuid(),
                [API_REQUEST_HEADERS.AUTHORIZATION]: `Bearer ${authorizationToken}`
              }
            }
          )

          if (result.data && typeof result.data.token === 'string') {
            setSupportSessionToken(result.data.token)
            return result.data.token
          } else {
            throw new Error('No support session token returned from the API.')
          }
        } catch (error) {
          throw new Error('Error refreshing support session token:', {
            cause: error
          })
        }
      }
      return supportSessionToken
    }

    return {
      // Reactive references
      supportSessionId,
      supportSessionToken,

      // Computed properties
      isSupportSession,

      // Utility functions
      setSupportSessionId,
      setSupportSessionToken,
      clearSupportSession,
      getSupportSessionId,
      getSupportSessionToken,

      // Fetch support session token
      fetchSupportSessionTokenIfExpired,

      // Storage keys (for cases where direct access is needed)
      STORAGE_KEYS: SUPPORT_SESSION_STORAGE_KEYS
    }
  }

  export function createSupportSessionContext() {
    const supportSessionStorage = useSupportSessionStorage()
    const supportSessionFromRouteQuery = useSupportSessionFromRouteQuery(
      supportSessionStorage.supportSessionId,
      supportSessionStorage.setSupportSessionId
    )

    return {
      ...supportSessionStorage,
      ...supportSessionFromRouteQuery,

      // Storage keys (for cases where direct access is needed)
      STORAGE_KEYS: SUPPORT_SESSION_STORAGE_KEYS
    }
  }

  export type SupportSessionContext = ReturnType<
    typeof createSupportSessionContext
  >

  export const supportSessionContextKey = Symbol(
    'gs-support-session-context'
  ) as InjectionKey<SupportSessionContext>

  /**
   * @param options - Options for the support session.
   * @param options.inject - If false, the support session will not be injected into the context, and will only use the storage.
   * @returns The support session context.
   */
  export function useSupportSession<
    T extends { inject?: boolean } = { inject?: boolean }
  >(
    options?: T
  ): T extends { inject: false }
    ? ReturnType<typeof useSupportSessionStorage>
    : SupportSessionContext {
    if (options?.inject === false) {
      return useSupportSessionStorage() as any
    }
    const instance = inject(supportSessionContextKey)

    if (!instance) {
      throw new Error('Support session context not found')
    }

    return instance as any
  }
</script>
<script setup lang="ts">
  const supportSessionContext = createSupportSessionContext()
  provide(supportSessionContextKey, supportSessionContext)
  defineExpose(supportSessionContext)
</script>
<template>
  <slot />
</template>
